import { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import CheckBoxHookForm from "../../../../components/CheckBoxHookForm";
import SubmitHookForm from "../../../../components/SubmitHookForm";
import CancelButton from "../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../components/ConfirmBoxModal";
import ConflictingDatesModal from "../../../../components/ConflictingDatesModal";
import ConfirmBoxYesNo from "../../../../components/ConfirmBoxYesNo";
import Tooltip from "react-tooltip";
import {
  agmtAddState,
  agmtEditMassUpload,
  agmtEditState,
  agmtMassUploadData,
  agreementForEdit,
  agmtCommisions,
  agmtSelectedStates,
  enableGetAgmt,
  agmtAllMassUploadData,
  agmtGrpFilterParams,
  agmtToExpire,
  editRenewedAgmt,
  conflictingAgreementId,
  filterConflictingAgreement,
  agmtShowGroupState,
  agreementFormData,
} from "../atoms/atoms";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import { toast } from "../../../../components/toastNotification";
import { useStates } from "../../hooks/useStates";
import { useAgencyFromAgencyMapping } from "../hooks/useAgencyFromAgencyMapping";
import { useEntity } from "../hooks/useEntity";
import { useCarrierProgram } from "../../../Utilities/ProducerConfiguration/CarrierProgram/hooks/useCarrierProgram";
import useGetRisk from "../../../Utilities/ConfigureAgreement/Risk/hooks/useGetRisk";
import useGetBusinessTypes from "../../../Utilities/ConfigureAgreement/BusinessType/hooks/useGetBusinessTypes";
import useGetLineOfBusiness from "../../../Utilities/ConfigureAgreement/LineOfBusiness/hooks/useGetLineOfBusiness";
import usePolicyType from "../../../Utilities/ConfigureAgreement/PolicyType/hooks/usePolicyType";
import useGetStandard from "../../../Utilities/ConfigureAgreement/Standard/hooks/useGetStandard";
import useGetLicenseType from "../../../Utilities/ConfigureAgreement/LicenseType/hooks/useGetLicenseType";
import useEditAgreement from "../hooks/useEditAgreement";
import useAddAgreements from "../hooks/useAddAgreement";
import useValidateData from "../hooks/useValidateData";
import AddCommissionDetails from "./AddCommissionDetails";
import AddAttachmentsButton from "./AddAttachmentsButton";
import { useQueryClient } from "react-query";
import { useGetAgreementComm } from "../hooks/useGetAgreementComm";
import { useEmployeesOfficeState } from "../../Employees/Confie/hooks/useEmployeesOfficeState";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import NumericInputLabel from "../../../../components/NumericInputLabel";
import NumericInputLabel2 from "components/NumericInputLabel2";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import { useAppointedBy } from "../hooks/useAppointedBy";
import { useAgreementType } from "../hooks/useAgreementType";
import { EntityModal } from "../modals/EntityModal";
import SelectHookFormMulti from "../../../../components/SelectHookFormMulti";
import { AgencyModal } from "../modals/AgencyModal";
import CheckBoxLabelHookForm from "../../../../components/CheckBoxLabelHookForm";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import CustomLoader from "../../../../components/CustomLoader";
import useAddRenewedAgreements from "../hooks/useAddRenewedAgreements";
import useSendEmail from "../hooks/useSendEmail";
import { useGetCommisionOfPayments } from "../hooks/useGetCommisionOfPayments";
import { useCommissionRemittance } from "../hooks/useCommissionRemittance";
import { useAgmtPaymentRemittanceType } from "../../../Utilities/ConfigureAgreement/PaymentRemittanceType/hooks/useGetPaymentRemittanceType";
import { useGetCarrierNameList } from "modules/CarrierAgreementMgmt/Carrier/hooks/useGetCarrierNameList";
import {
  slidingScaleDuplicateChecker,
  SlidingScaleForm,
  slidingScaleFormState,
  errorSlidingScale,
} from "./SlidingScale";
import { useGetAllMasterLegalEntities } from "../hooks/useGetMasterLegalEntity";
import { useGetAllOverridePaymentTypes } from "../hooks/useGetOverridePayment";

const AgreementForm = ({ getAgmtRefetch }) => {
  const user = useRecoilValue(accountAtom);
  const userPermissions = useRecoilValue(permissionsSelector);
  const comValStatusPermssion = PERMISSIONS.permission_agmtField_comValStatus;
  const queryClient = useQueryClient();
  const [isAddState, setAddState] = useRecoilState(agmtAddState);
  const [isEditState, setEditState] = useRecoilState(agmtEditState);
  const [isSaving, setIsSaving] = useState(false);
  const [, setEnabledGetAgreement] = useRecoilState(enableGetAgmt);
  const [rowForEdit, setRowToEdit] = useRecoilState(agreementForEdit);
  const [agreementData, setAgreementData] = useState([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const defaultData = rowForEdit; // for edit default values
  const [isEditMassUpload, setEditMassUpload] =
    useRecoilState(agmtEditMassUpload);
  const [massUploadData, setMassUploadData] =
    useRecoilState(agmtMassUploadData);
  const [, setAllMassUploadData] = useRecoilState(agmtAllMassUploadData);
  const [forRevalMassUpload, setForRevalMassUpload] = useState([]);
  const [, setSelectedStates] = useRecoilState(agmtSelectedStates);
  const [getSFilesNComm, setGetSFilesNComm] = useState(isEditState);
  const [newAttachmentRow, setNewAttachmentRow] = useState(); //array for attachment form
  const [agmtSourceFiles, setAgmtSourceFiles] = useState(); //files to be save in sourceFile db
  const [filesForUpload, setFilesForUpload] = useState(); //formdata files to be saved in ftp
  const [commissions, setCommissions] = useRecoilState(agmtCommisions);
  const slidingScale = useRecoilValue(slidingScaleFormState);
  const [slidingScaleFormData, setSlidingScaleFormData] = useRecoilState(
    slidingScaleFormState
  );
  const isErrorSlidingScale = useSetRecoilState(errorSlidingScale);
  const [oldCommissions, setOldCommissions] = useState([]);
  const [entityModalShow, setEntityModalShow] = useState(false);
  const [agencyShow, setAgencyShow] = useState(false);
  const [legalEntities, setLegalEntities] = useState([]);
  const [agencyDetails, setAgencyDetails] = useState([]);
  const [minExpire, setMinExpire] = useState([]);
  const [filterGrpParams, setFilterGrpParams] =
    useRecoilState(agmtGrpFilterParams);

  const [toExpire, setToExpire] = useRecoilState(agmtToExpire);
  const [openConfirmNew, setOpenConfirmNew] = useState(false);
  const [openConflictingDate, setOpenConflictingDate] = useState(false);
  const [isSlidingScaleChecked, setIsSlidingScaleChecked] = useState(
    rowForEdit.isSlidingScale ?? false
  );
  const [currentSplitRateValue, setCurrentSplitRateValue] = useState(
    rowForEdit.isSplitRate ?? false
  );
  const [isOverridePayment, setIsOverridePayment] = useState(
    rowForEdit.isOverridePayment ?? false
  );
  const [policyType, setPolicyType] = useState([]);

  const [allState, setAllState] = useState(false);
  const [allPolicy, setAllPolicy] = useState(false);
  const [previousSelected, setPreviousSelected] = useState([]);

  const isOtherChecked = isSlidingScaleChecked || currentSplitRateValue;
  const [, setEditRenewed] = useRecoilState(editRenewedAgmt);
  const [, setConflictingAgreementIds] = useRecoilState(conflictingAgreementId);
  const [, setFilterConflictingAgreements] = useRecoilState(
    filterConflictingAgreement
  );
  const [savedAgreementData, setSavedAgreementData] =
    useRecoilState(agreementFormData);
  const [, setShowGroupView] = useRecoilState(agmtShowGroupState);
  const [isManuallyDirty, setIsManuallyDirty] = useState(false);

  const { mutateAsync: addAgreement } = useAddAgreements(); //function calling add agreement
  const { mutateAsync: editAgreement } = useEditAgreement(); //function calling edit agreement
  const { mutateAsync: validateMassAgmt } = useValidateData(); //function for re-validating edited mass upload agreement
  const { mutateAsync: insertRenewedAgmts } = useAddRenewedAgreements(); //function for renewal of expired agreements
  const {
    formState: { errors, isDirty },
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm(
    isEditState || isEditMassUpload ? { defaultValues: defaultData } : ""
  );
  const { mutateAsync: sendEmail } = useSendEmail();
  //DropDown Options
  const { data: carriers, isLoading: carriersLoading } =
    useGetCarrierNameList();
  const carrierOptions = Array.from(
    new Map(
      carriers
        ?.filter((x) => x.isActive === true)
        ?.map((x) => [
          x.masterCarrierId,
          {
            label: x.carrierName,
            value: x.masterCarrierId,
          },
        ])
    ).values()
  );

  //for All option in States
  const allDropdownOption = {
    label: "ALL",
    value: "ALL",
  };
  const { data: states, isLoading: statesLoading } = useStates();
  const stateOptions = states?.map((x) => {
    return {
      label: x.state,
      value: x.state,
    };
  });

  const { data: masterLegalEntity, isLoading: masterLegalEntityLoading } =
    useGetAllMasterLegalEntities();
  const masterLegalEntityOpt = masterLegalEntity?.map((x) => {
    return {
      label: x.entityName,
      value: x.entityId,
    };
  });

  const { data: overridePaymentType, isLoading: overridePaymentTypeLoading } =
    useGetAllOverridePaymentTypes();
  const overridePaymentTypeOpt = overridePaymentType?.map((x) => {
    return {
      label: x.paymentType,
      value: x.overridePaymentTypeId,
    };
  });

  const allStatesOpt = stateOptions && [allDropdownOption, ...stateOptions]; //add all option to states dropdown

  // for All option in Policy type
  const { data: policies, isLoading: policyLoading } = usePolicyType();

  useEffect(() => {
    const policyTypes = policies
      ?.filter((x) => x.isActive === true)
      ?.map((data) => {
        return {
          label: data.policyTypeName,
          value: data.policyTypeName,
        };
      });

    setPolicyType(policyTypes && [allDropdownOption, ...policyTypes]);
  }, [policies, policyLoading]);

  const { data: agenciesFromAgencyMapping, isLoading: agencyLoading } =
    useAgencyFromAgencyMapping();
  const agencyOptions = agenciesFromAgencyMapping?.map((a) => {
    return {
      label: a.agencyName,
      value: a.agencyName,
      stateCode: a.stateCode,
      agencyMappingId: a.agencyMappingID,
    };
  });
  const allAgencyOption = {
    label: "ALL",
    value: "ALL",
    stateCode: "ALL",
  };
  const filteredAgencies = watch("state")
    ?.map((x) => x.value)
    .includes("ALL")
    ? agencyOptions?.filter(
        (row, index, self) =>
          self.findIndex((v) => v.label === row.label) === index //filters duplicate labels
      )
    : agencyOptions
        ?.filter((agn) => {
          return watch("state")
            ?.map((x) => x.label)
            .includes(agn.stateCode);
        })
        ?.filter(
          (row, index, self) =>
            self.findIndex((v) => v.label === row.label) === index //filters duplicate labels
        );
  const filteredAgenciesWithAll = filteredAgencies && [
    allAgencyOption,
    ...filteredAgencies,
  ];

  const { data: AppointedBy } = useAppointedBy();
  const appointedByOptions = AppointedBy?.map((a) => {
    return {
      label: a.appointedBy1,
      value: a.appointedBy1,
    };
  });
  const { data: agreementTypes } = useAgreementType();
  const { data: commissionRemittanceType } = useCommissionRemittance();
  const agreementTypeOptions = agreementTypes?.map((a) => {
    return {
      label: a.agreementTypeName,
      value: a.agreementTypeName,
    };
  });
  const commissionRemittanceOption = commissionRemittanceType?.map((a) => {
    return {
      label: a.name,
      value: a.id,
    };
  });
  const { data: entities, isLoading: entityLoading } = useEntity();

  const entityOptions = entities?.map((ent) => {
    return {
      label: ent.entityName,
      value: ent.entityName,
      stateCode: ent.stateCode,
      entityId: ent.entityId,
      isActive: ent.isActive,
    };
  });

  const { data: standards } = useGetStandard();
  const standardTypes = standards
    ?.filter((x) => x.isActive === true)
    .map((a) => {
      return { label: a.standardName, value: a.standardName };
    });
  const { data: licenseTypes } = useGetLicenseType();
  const licensedTypes = licenseTypes
    ?.filter((x) => x.isActive === true)
    .map((data) => {
      return { label: data.licenseTypeName, value: data.licenseTypeId };
    });
  const { data: empOfficeStates, isLoading: employeesLoading } =
    useEmployeesOfficeState();

  const employees = empOfficeStates?.map((x) => {
    return {
      value: x.employeeId,
      label: x.fullname + " - " + x.employeenumber,
      state: x.stateCode,
      employeeName: x.fullname,
      isFranchise: x.employeeTypeName === "Franchise" ? true : false,
    };
  });
  const { data: lobs, isLoading: lobsLoading } = useGetLineOfBusiness();

  const { data: businessTypes, isLoading: businessLoading } =
    useGetBusinessTypes();

  const businessTypesOptions = businessTypes?.data
    .filter((business) => business.isActive === true)
    .map((x) => {
      return { label: x.businessTypeName, value: x.businessTypeId };
    });

  const { data: programs, isLoading: programsLoading } = useCarrierProgram();
  const programOptions =
    programs &&
    programs
      ?.filter((x) => x.isActive === true)
      .map((x) => {
        return {
          label: x.programName,
          value: x.programName,
          carrierId: x.carrierId,
        };
      });
  const { data: riskData, isLoading: riskLoading } = useGetRisk();
  const riskTypes =
    { ...riskData }.data &&
    { ...riskData }.data
      ?.filter((x) => x.isActive === true)
      .map((x) => {
        return {
          label: x.riskName,
          value: x.riskId,
        };
      });
  const paidTypeOptions = [
    { label: "As Earned", value: "As Earned" },
    { label: "As Written", value: "As Written" },
    { label: "As Collected", value: "As Collected" },
    { label: "Flat Rate", value: "Flat Rate" },
  ];

  const { data: commPaymentFreq } = useGetCommisionOfPayments();
  const commPaymentFreqOpt = commPaymentFreq?.data?.map((x) => {
    return {
      label: x.name,
      value: x.id,
    };
  });

  const billingTypes = [
    { label: "Agency", value: "Agency" },
    { label: "Direct", value: "Direct" },
    { value: "Direct / Agency", label: "Direct / Agency" },
  ];

  const { data: paymentRemittanceTypes } = useAgmtPaymentRemittanceType();
  const paymentRemittanceTypesOptions = paymentRemittanceTypes
    ?.filter((x) => x.status === true)
    ?.map((x) => {
      return {
        label: x.paymentRemittanceTypeName,
        value: x.paymentRemittanceTypeId,
      };
    });

  const statusTypes = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const comValStatusTypes = [
    { value: true, label: "True" },
    { value: false, label: "False" },
  ];

  // END DropDown Options

  // For conflicting agreement redirection process
  const isOrigAgreement = useMemo(() => {
    return (
      savedAgreementData &&
      rowForEdit?.agreementRepositoryId &&
      Number(savedAgreementData.agreementRepositoryId) ===
        Number(rowForEdit.agreementRepositoryId)
    );
  }, [savedAgreementData, rowForEdit]);

  //Register fields not included inside hookform
  useEffect(() => {
    register("sourceFiles");
    register("otherCommissionPercentageTransactionList");
    register("pifMinList");
    register("pifMaxList");

    // Restore saved form data if available
    if (isAddState) {
      if (savedAgreementData && savedAgreementData.masterCarrierId > 0) {
        for (const key of Object.keys(savedAgreementData)) {
          setValue(key, savedAgreementData[key]);
        }
      }
      setValue("isActive", true);
      setValue("agreementIsActive", true);
      if (user.isFranchise && !user.isNonFranchise) {
        setValue("isFranchise", 1);
      }
    } else if (isOrigAgreement) {
      reset(savedAgreementData);
      setRowToEdit(savedAgreementData.origData);
      setIsManuallyDirty(true);
    }

    if (watch("expirationDate") < new Date().toISOString().slice(0, 10)) {
      setValue("isWritingNewBusiness", false);
    }
  }, [
    isAddState,
    savedAgreementData,
    isOrigAgreement,
    user.isFranchise,
    user.isNonFranchise,
    watch,
  ]);

  useEffect(() => {
    //Handles Legal Entity and Agency Data for Modal
    if (watch("state") && watch("state")?.length > 0) {
      const selectedState = watch("state")?.map((x) => x.label?.toUpperCase());

      if (watch("entityName") && entities) {
        if (selectedState.includes("ALL")) {
          setLegalEntities(
            entities
              ?.filter(
                (x) =>
                  x.entityName?.toLowerCase() ===
                  watch("entityName")?.toLowerCase()
              )
              .sort((a, b) =>
                a.stateCode?.toLowerCase() > b.stateCode?.toLowerCase() ? 1 : -1
              )
          );
        } else {
          const selectedEntities = entities?.filter(
            (x) =>
              x.entityName.toLowerCase() ===
                watch("entityName")?.toLowerCase() &&
              selectedState.includes(x.stateCode)
          );
          setLegalEntities(
            selectedEntities
              .sort((a, b) =>
                a.stateCode.toLowerCase() > b.stateCode.toLowerCase() ? 1 : -1
              )
              .sort((a, b) =>
                a.entityName.toLowerCase() > b.entityName.toLowerCase() ? 1 : -1
              )
          );
        }
      }
      if (watch("agency") && agenciesFromAgencyMapping) {
        if (selectedState.includes("ALL")) {
          if (watch("agency")?.toUpperCase() === "ALL") {
            setAgencyDetails(
              agenciesFromAgencyMapping
                ?.filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode.toLowerCase() > b.stateCode.toLowerCase() ? 1 : -1
                )
                .sort((a, b) =>
                  a.agencyName.toLowerCase() > b.agencyName.toLowerCase()
                    ? 1
                    : -1
                )
            );
          } else {
            setAgencyDetails(
              agenciesFromAgencyMapping
                ?.filter(
                  (x) =>
                    x.agencyName.toLowerCase() ===
                    watch("agency")?.toLowerCase()
                )
                ?.filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode.toLowerCase() > b.stateCode.toLowerCase() ? 1 : -1
                )
                .sort((a, b) =>
                  a.agencyName.toLowerCase() > b.agencyName.toLowerCase()
                    ? 1
                    : -1
                )
            );
          }
        } else {
          if (watch("agency")?.toUpperCase() === "ALL") {
            setAgencyDetails(
              agenciesFromAgencyMapping
                ?.filter((x) => selectedState.includes(x.stateCode))
                ?.filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode.toLowerCase() > b.stateCode.toLowerCase() ? 1 : -1
                )
                .sort((a, b) =>
                  a.agencyName.toLowerCase() > b.agencyName.toLowerCase()
                    ? 1
                    : -1
                )
            );
          } else {
            setAgencyDetails(
              agenciesFromAgencyMapping
                ?.filter(
                  (x) =>
                    x.agencyName?.toLowerCase() ===
                      watch("agency")?.toLowerCase() &&
                    selectedState.includes(x.stateCode)
                )
                ?.filter(
                  (row, index, self) =>
                    self.findIndex(
                      (v) =>
                        v.agencyName === row.agencyName &&
                        v.stateCode === row.stateCode
                    ) === index //filter duplicate agencyName and states
                )
                .sort((a, b) =>
                  a.stateCode.toLowerCase() > b.stateCode.toLowerCase() ? 1 : -1
                )
                .sort((a, b) =>
                  a.agencyName.toLowerCase() > b.agencyName.toLowerCase()
                    ? 1
                    : -1
                )
            );
          }
        }
      }
    } else {
      setLegalEntities([]);
      setAgencyDetails([]);
    }
  }, [
    entities,
    agenciesFromAgencyMapping,
    watch("state"),
    watch("agency"),
    watch("entityName"),
  ]);

  //const checkedStates = watch("state", []); //will get selected states value

  const filteredEntityOpt = watch("state")
    ?.map((x) => x.value)
    .includes("ALL")
    ? entityOptions?.filter(
        (row, index, self) =>
          self.findIndex((v) => v.label === row.label) === index
      )
    : entityOptions
        ?.filter((ent) => {
          return (
            watch("state")
              ?.map((x) => x.label)
              .includes(ent.stateCode) && ent.isActive === true
          );
        })
        ?.filter(
          (row, index, self) =>
            self.findIndex((v) => v.label === row.label) === index
        );

  const filteredSalesLEad =
    watch("state")
      ?.map((x) => x.value)
      ?.includes("ALL") &&
    user.isNonFranchise &&
    user.isFranchise
      ? employees?.filter((emp) => emp.isFranchise === watch("isFranchise"))
      : user.isNonFranchise && user.isFranchise && !watch("isFranchise")
      ? employees?.filter((emp) => {
          return watch("state")
            ?.map((x) => x.value)
            .includes(emp.state);
        })
      : watch("state")
          ?.map((x) => x.value)
          ?.includes("ALL")
      ? employees?.filter((emp) => emp.isFranchise === user.isFranchise)
      : user.isFranchise
      ? employees?.filter((emp) => {
          return (
            watch("state")
              ?.map((x) => x.value)
              .includes(emp.state) && emp.isFranchise === true
          );
        })
      : employees?.filter((emp) => {
          return (
            watch("state")
              ?.map((x) => x.value)
              .includes(emp.state) && emp.isFranchise === false
          );
        });

  //filtered PROGRAM based on MasterCarrier
  const filteredProgramOptions = watch("masterCarrierId")
    ? programOptions?.filter((x) => x.carrierId === watch("masterCarrierId"))
    : [];
  //filtered LOB based on businessType
  const lobOptions = !watch("businessTypeId")
    ? []
    : lobs
        ?.filter(
          (lob) =>
            lob.isActive === true &&
            lob.businessTypeId === watch("businessTypeId")
        )
        .map((x) => {
          return { label: x.lineOfBusinessName, value: x.lineOfBusinessId };
        });
  //Set existing other commissions
  const {
    status: commissionsStatus,
    data: commissionsData,
    isLoading: otherCommsLoading,
  } = useGetAgreementComm(rowForEdit.agreementRepositoryId, getSFilesNComm);
  useEffect(() => {
    setSelectedStates(rowForEdit.state);
    //Sets default attachments for edit

    setAgmtSourceFiles(rowForEdit.sourceFiles);
    if (rowForEdit.sourceFiles) {
      setNewAttachmentRow(() => {
        const files = { sourceLink: rowForEdit.sourceFiles?.sourceLink };
        return {
          Filename: files,
        };
      });
    }

    //Sets default other commissions for edit
    if (commissionsStatus === "success" && commissionsData) {
      if (
        commissionsData.pifMin !== null &&
        commissionsData.pifMin !== undefined
      ) {
        if (commissionsData.pifMin.length > 0) {
          setCommissions(
            commissionsData.pifMin.map((x, i) => {
              return {
                contingencyCommission:
                  commissionsData.otherCommissionPercentage[i],
                contingencyCommissionTransaction:
                  commissionsData.otherCommissionTransaction[i],
                pifMin: commissionsData.pifMin[i],
                pifMax: commissionsData.pifMax[i],
              };
            })
          );

          setOldCommissions(
            commissionsData.pifMin.map((x, i) => {
              return {
                contingencyCommission:
                  commissionsData.otherCommissionPercentage[i],
                contingencyCommissionTransaction:
                  commissionsData.otherCommissionTransaction[i],
                pifMin: commissionsData.pifMin[i],
                pifMax: commissionsData.pifMax[i],
              };
            })
          );
        }
      }
      setGetSFilesNComm(false);
    }
  }, [
    commissionsData,
    commissionsStatus,
    rowForEdit.sourceFiles,
    rowForEdit.state,
  ]);

  //handles multi states selections
  const statesOnChange = (e) => {
    setValue("agency", null);
    setValue("entityName", null);
    setValue("legalEntities", []);
    setValue("salesLead", null);
  };
  //handles save button
  const onFormSubmit = (data) => {
    const keysToExclude = [
      "otherCommissionPercentageTransactionList",
      "pifMinList",
      "pifMaxList",
    ];
    const oldData = JSON.stringify(rowForEdit);
    const newData = JSON.stringify(
      Object.keys(data).reduce((acc, key) => {
        if (!keysToExclude.includes(key)) {
          acc[key] = data[key];
        }
        return acc;
      }, {})
    );

    if (isEditMassUpload) {
      if (isDirty) {
        if (oldData === newData) {
          toast.fire("Saving Failed", "No Changes have been made", "warning");
        } else {
          if (typeof data.notes === "string") {
            data.notes = data.notes.trim();
          }
          setForRevalMassUpload(
            massUploadData.map((item) => {
              if (item.id === data.id) {
                return {
                  ...data,
                  masterCarrierId: data.masterCarrierId,
                  state: data.state.map((x) => x.value).includes("ALL")
                    ? "ALL"
                    : data.state.map((s) => s.value).join(","),
                  policyType: data.policyType
                    .map((x) => x.value)
                    .includes("ALL")
                    ? "ALL"
                    : data.policyType.map((s) => s.value).join(","),
                  condition: "revalidate",
                };
              } else {
                return item;
              }
            })
          );
          setOpenConfirm(true);
        }
      } else {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    } else {
      const notEmptyComms = commissions?.filter(
        (x) =>
          x.contingencyCommission ||
          x.contingencyCommissionTransaction ||
          x.pifMax ||
          x.pifMin
      );
      if (notEmptyComms.length !== commissions.length) {
        toast.fire(
          "Other Commissions",
          "Empty other commission row is not allowed",
          "error"
        );
      } else {
        //converts commissions to string to compare if values has changes
        const oldComms = JSON.stringify(oldCommissions);
        const newComms = JSON.stringify(commissions);
        const oldSlidingScale = JSON.stringify(rowForEdit.slidingScale);
        const newSlidingScale = slidingScale.fields.some(
          (field) => field.premiumRangeStart === null
        )
          ? "[]"
          : JSON.stringify(
              slidingScale.fields.map(({ index, ...rest }) => rest)
            );

        const oldData = JSON.stringify(rowForEdit);
        const submittedData = JSON.stringify(
          Object.keys(data).reduce((acc, key) => {
            if (!keysToExclude.includes(key)) {
              acc[key] = data[key];
            }
            return acc;
          }, {})
        );

        if (isEditState) {
          if (
            data.masterCarrierId !== defaultData.masterCarrierId ||
            data.effectiveDate !== defaultData.effectiveDate ||
            data.expirationDate !== defaultData.expirationDate ||
            data.newPercentage !== defaultData.newPercentage ||
            data.newPercentageConfie !== defaultData.newPercentageConfie ||
            data.newPercentageFranchise !==
              defaultData.newPercentageFranchise ||
            data.renewalPercentage !== defaultData.renewalPercentage ||
            data.renewPercentageConfie !== defaultData.renewPercentageConfie ||
            data.renewPercentageFranchise !==
              defaultData.renewPercentageFranchise ||
            data.flatRatePerPolicy !== defaultData.flatRatePerPolicy ||
            data.newBusinessFlatRate !== defaultData.newBusinessFlatRate ||
            data.renewalFlatRate !== defaultData.renewalFlatRate
          ) {
            if (filterGrpParams.AgreementRepositoryId.length > 1) {
              setFilterGrpParams({
                ...filterGrpParams,
                AgreementRepositoryId:
                  filterGrpParams.AgreementRepositoryId.filter(
                    (x) => x !== parseInt(data.agreementRepositoryId)
                  ),
              });
            }
          }

          if (isSlidingScaleChecked && slidingScaleFormData.fields.length > 0) {
            const nullChecker = slidingScaleFormData.fields.some(
              (x) =>
                x.premiumRangeStart === null ||
                x.premiumRangeEnd === null ||
                x.percentageRate === null
            );

            const duplicate = slidingScaleDuplicateChecker(
              slidingScaleFormData.fields
            );

            if (nullChecker > 0) {
              toast.fire(
                "Warning",
                "Please fill out all Sliding Scales fields",
                "warning"
              );
              isErrorSlidingScale(true);
              setOpenConfirm(false);
              return;
            } else {
              isErrorSlidingScale(false);
            }

            if (duplicate) {
              toast.fire(
                "Warning",
                "Duplicate Sliding Scale related fields",
                "warning"
              );
              isErrorSlidingScale(true);
              setOpenConfirm(false);
              return;
            } else {
              isErrorSlidingScale(false);
            }
          }

          const newAttachRow =
            newAttachmentRow && newAttachmentRow.Filename.sourceLink;

          //Save Edited Agreement
          const checkForChanges = isDirty || isManuallyDirty;
          if (
            ((rowForEdit.sourceFiles &&
              newAttachRow !== rowForEdit.sourceFiles.sourceLink) ||
              filesForUpload ||
              oldComms !== newComms ||
              oldData !== submittedData ||
              oldSlidingScale !== newSlidingScale) &&
            checkForChanges
          ) {
            setAgreementToSave({ data });
            setOpenConfirm(true);
          } else {
            toast.fire("Saving Failed", "No Changes have been made", "warning");
            reset();
          }
        } else {
          if (isSlidingScaleChecked && slidingScaleFormData.fields.length > 0) {
            const nullChecker = slidingScaleFormData.fields.some(
              (x) =>
                x.premiumRangeStart === null ||
                x.premiumRangeEnd === null ||
                x.percentageRate === null
            );

            const duplicate = slidingScaleDuplicateChecker(
              slidingScaleFormData.fields
            );

            if (nullChecker > 0) {
              toast.fire(
                "Warning",
                "Please fill out all Sliding Scales fields",
                "warning"
              );
              isErrorSlidingScale(true);
              setOpenConfirm(false);
              return;
            } else {
              isErrorSlidingScale(false);
            }

            if (duplicate) {
              toast.fire(
                "Warning",
                "Duplicate Sliding Scale related fields",
                "warning"
              );
              isErrorSlidingScale(true);
              setOpenConfirm(false);
              return;
            } else {
              isErrorSlidingScale(false);
            }
          }
          //Save New Agreement
          setAgreementToSave({ data });
          setOpenConfirm(true);
        }
      }
    }
  };

  const setAgreementToSave = ({ data }) => {
    const duplicateFilterCodePrefix = (key) => {
      const values = data[key].split(",").map((value) => value.trim());
      const uniqueValues = [
        ...new Set(values.map((value) => value.toLowerCase())),
      ].map((lowercase) =>
        values.find((value) => value.toLowerCase() === lowercase)
      );
      return uniqueValues.join(",");
    };

    const policyPrefix = duplicateFilterCodePrefix("policyPrefix");
    const producerCode = duplicateFilterCodePrefix("producerCode");

    setAgreementData({
      ...data,
      policyType: data.policyType.map((x) => x.value).includes("ALL")
        ? "ALL"
        : data.policyType.map((s) => s.value).join(","),
      policyPrefix,
      producerCode,
      slidingScales: slidingScale.fields.map((x) => {
        return x;
      }),
      agreementRepositoryId: toExpire
        ? [parseInt(data.agreementRepositoryId)]
        : parseInt(data.agreementRepositoryId),
      masterCarrierId: data.masterCarrierId,
      isFranchise: data.isFranchise > 0 ? true : false,
      state: data.state.map((x) => x.value).includes("ALL")
        ? "ALL"
        : data.state.map((s) => s.value).join(","),
      sourceFiles: agmtSourceFiles,
      attachments: filesForUpload,
      newPercentageConfie: data.isFranchise ? data.newPercentageConfie : 0,
      newPercentageFranchise: data.isFranchise
        ? data.newPercentageFranchise
        : 0,
      renewPercentageConfie: data.isFranchise ? data.renewPercentageConfie : 0,
      renewPercentageFranchise: data.isFranchise
        ? data.renewPercentageFranchise
        : 0,
      newPercentage: data.newPercentage ? data.newPercentage : 0,
      renewalPercentage: data.renewalPercentage ? data.renewalPercentage : 0,
      otherCommissionPercentageList: commissions.map((c) => {
        return c.contingencyCommission;
      }),
      otherCommissionPercentageTransactionList: commissions.map((c) => {
        return c.contingencyCommissionTransaction;
      }),
      pifMinList: commissions.map((c) =>
        c.pifMin ? parseInt(c.pifMin) : c.pifMin
      ),
      pifMaxList: commissions.map((c) =>
        c.pifMin ? parseInt(c.pifMax) : c.pifMin
      ),
    });
  };

  const handleSave = (conflictingAgreements) => {
    setIsSaving(true);
    setOpenConfirm(false);
    setOpenConfirmNew(false);

    if (isAddState) {
      saveAgreement(false, conflictingAgreements);
    } else if (isEditState) {
      updateAgreement(false, conflictingAgreements);
    } else if (isEditMassUpload) {
      if (isDirty) {
        validateMasUpload();
      }
    }
  };

  const saveAgreement = (saveRenewedAgmt = false, conflictingAgreements) => {
    addAgreement(
      {
        ...agreementData,
        isRenewal: saveRenewedAgmt,
        isConflictingAgreement: conflictingAgreements,
      },
      {
        onSuccess: (res) => {
          if (res) {
            if (res.data === "duplicate") {
              toast.fire(
                "Adding new Agreement failed",
                "Agreement already exist",
                "error"
              );
              setIsSaving(false);
            } else if (res.data?.message === "Conflicting Dates") {
              setConflictingAgreementIds(res.data?.agreementIds);
              setOpenConflictingDate(true);
            } else if (res.status === 200 || res.status === 201) {
              setFilterConflictingAgreements(false);
              setSavedAgreementData([]);
              sendEmail(res.data, {
                onSuccess: (emailRes) => {
                  if (emailRes) {
                    if (emailRes.status === 400) {
                      toast.fire(
                        "Success",
                        "Agreement Added Successfully, but failed in sending email",
                        "warning"
                      );
                    } else {
                      if (saveRenewedAgmt) {
                        toast.fire(
                          "Success",
                          "Agreement Added Successfully",
                          "success"
                        );

                        saveRenewedAgreement({
                          ...agreementData,
                          agreementRepositoryId: res.data.ids,
                        });
                      } else {
                        setIsSaving(false);
                        setEnabledGetAgreement(true);
                        setEditRenewed(false);
                        handleCancel();
                        setConflictingAgreementIds([]);
                        toast.fire(
                          "Success",
                          "Agreement Added Successfully",
                          "success"
                        );
                        getAgmtRefetch();
                      }
                    }
                  } else {
                    toast.fire(
                      "Success",
                      "Agreement Added Successfully, but failed in sending email",
                      "warning"
                    );
                  }
                },
              });
            } else if (res.status === 500) {
              toast.fire(
                "Adding New Agreement Failed",
                "Server Error: Cannot update new Agreement, " +
                  res.data.message,
                "error"
              );
            } else {
              toast.fire(
                "Adding new Agreement failed",
                "Server Error: Cannot add new Agreement",
                "error"
              );
            }
          }
        },
        onError: (error) => {
          setIsSaving(false);
          if (error) {
            if (error.response && error.response.data) {
              if (
                error.response.data.message === "Failed sending of email" ||
                error.response.data.message ===
                  "Failed inserting of notification"
              ) {
                toast.fire(
                  "Success",
                  "New Agreement added successfully, but " +
                    error.response.data.message,
                  "warning"
                );
              }
            } else {
              toast.fire(
                "Adding New Agreement failed",
                "Server Error: Cannot add new Agreement",
                "error"
              );
            }
          }
        },
      }
    );
  };
  const updateAgreement = (saveRenewedAgmt = false, ConflictingAgreements) => {
    editAgreement(
      {
        ...agreementData,
        isRenewal: saveRenewedAgmt,
        isConflictingAgreement: ConflictingAgreements,
      },
      {
        onSuccess: (res) => {
          if (res) {
            if (res.data === "duplicate") {
              toast.fire(
                "Updating new Agreement failed",
                "Another Agreement already has the same information",
                "error"
              );
              setIsSaving(false);
            } else if (res.data?.message === "Conflicting Dates") {
              setConflictingAgreementIds(res.data?.agreementIds);
              setOpenConflictingDate(true);
            } else if (res.status === 200 || res.status === 201) {
              sendEmail(res.data, {
                onSuccess: (res) => {
                  if (res) {
                    if (res.status === 400) {
                      toast.fire(
                        "Success",
                        "Agreement Updated Successfully, but failed in sending email",
                        "warning"
                      );
                    } else {
                      queryClient.invalidateQueries("GetMainAgreements");
                      getAgmtRefetch();
                      toast.fire(
                        "Success",
                        "Agreement Updated Successfully",
                        "success"
                      );
                    }
                  } else {
                    toast.fire(
                      "Success",
                      "Agreement Updated Successfully, but failed in sending email",
                      "warning"
                    );
                  }
                  if (saveRenewedAgmt) {
                    saveRenewedAgreement();
                  } else {
                    setIsSaving(false);
                    setEditRenewed(false);
                    handleCancel();
                    queryClient.invalidateQueries("getAgreementComm");
                    setEnabledGetAgreement(true);
                  }
                },
              });
            } else if (res.status === 500) {
              if (
                res.data.message === "Failed sending of email" ||
                res.data.message === "Failed inserting of notification"
              ) {
                queryClient.invalidateQueries("getAgreementComm");
                setEnabledGetAgreement(true);
                handleCancel();
                toast.fire(
                  "Success",
                  "Agreement updated successfully, but " + res.data.message,
                  "warning"
                );
              } else {
                toast.fire(
                  "Updating Agreement Failed",
                  "Server Error: Cannot update Agreement, " + res.data.message,
                  "error"
                );
              }
            } else {
              toast.fire(
                "Updating Agreement Failed",
                "Server Error: Cannot update Agreement, " + res.data.message,
                "error"
              );
            }
          } else {
            toast.fire(
              "Updating Agreement failed",
              "Server Error: Cannot update new Agreement",
              "error"
            );
          }
        },
        onError: (error) => {
          setIsSaving(false);
          if (error) {
            if (
              error.response &&
              (error.response.data.message === "Failed sending of email" ||
                error.response.data.message ===
                  "Failed inserting of notification")
            ) {
              toast.fire(
                "Success",
                "Agreement updated successfully, but " +
                  error.response.data.message,
                "warning"
              );
            } else {
              toast.fire(
                "Adding new Agreement failed",
                "Server Error: Cannot add new Agreement",
                "error"
              );
            }
          }
        },
      }
    );
  };

  const saveRenewedAgreement = (renewedData = agreementData) => {
    setIsSaving(true);
    insertRenewedAgmts(
      {
        ...renewedData,
        asourceFiles: null,
        attachments: null,
      },
      {
        onSuccess: (res) => {
          if (res) {
            queryClient.invalidateQueries("getAgreementComm");

            if (res.status === 200) {
              sendEmail(res.data, {
                onSuccess: (emailRes) => {
                  if (emailRes) {
                    setIsSaving(false);
                    handleCancel();
                    setFilterGrpParams({
                      ...filterGrpParams,
                      AgreementRepositoryId: res.data,
                    });
                    queryClient.invalidateQueries("getAgreementComm");
                    setEnabledGetAgreement(true);
                    setEditRenewed(isEditState ? true : false);
                    if (emailRes.status === 400) {
                      toast.fire(
                        "Success",
                        "Agreement Added Successfully, but failed in sending email",
                        "warning"
                      );
                    } else {
                      toast.fire(
                        "Success",
                        "Renewed Agreement was added successfully",
                        "success"
                      );
                    }
                  } else {
                    toast.fire(
                      "Success",
                      "Agreement Added Successfully, but failed in sending email",
                      "warning"
                    );
                  }
                },
              });
              setEditState(false);
            } else if (res.status === 500) {
              if (
                res.data.message === "Failed sending of email" ||
                res.data.message === "Failed inserting of notification"
              ) {
                queryClient.invalidateQueries("getAgreementComm");
                setEnabledGetAgreement(true);
                handleCancel();
                toast.fire(
                  "Success",
                  "Renewed Agreement was added successfully, but " +
                    res.data.message,
                  "warning"
                );
              }
            } else {
              toast.fire(
                "Inserting Agreement Failed",
                "Server Error: Cannot insert renewed Agreement, " +
                  res.data.message,
                "error"
              );
            }
          }
        },
        onError: (error) => {
          setIsSaving(false);
          if (error) {
            toast.fire(
              "Adding Renewed Agreement Failed",
              "Server Error: Cannot add new Agreement",
              "error"
            );
          }
        },
      }
    );
  };
  const handleRenewAgreements = (conflictingAgreements, renewAgreement) => {
    setOpenConfirmNew(false);
    setOpenConfirm(false);
    setIsSaving(true);
    if (isEditState) {
      updateAgreement(renewAgreement, conflictingAgreements);
    }
    if (isAddState) {
      console.log("trigger");
      saveAgreement(renewAgreement, conflictingAgreements);
    }
    if (isEditMassUpload) {
      saveRenewedAgreement();
    }
  };
  const validateMasUpload = () => {
    validateMassAgmt(forRevalMassUpload, {
      onSuccess: (res) => {
        setIsSaving(false);
        if (res) {
          if (res.length > 0) {
            setRowToEdit([]);
            setEditMassUpload(false);
            setMassUploadData(res);
            setAllMassUploadData(res);
          } else {
            toast.fire(
              "Updating Agreement failed",
              "Server Error: Cannot update Agreement",
              "error"
            );
          }
        }
      },
      onError: (err) => {
        setIsSaving(false);
        toast.fire("Updating Agreement failed", err, "error");
      },
    });
  };

  const handleCancel = (row) => {
    queryClient.invalidateQueries("GetMainAgreements");

    if (isOrigAgreement || isAddState) {
      setSavedAgreementData([]);
    }

    getAgmtRefetch();
    setRowToEdit([]);
    setEditState(false);
    setAddState(false);
    setEditMassUpload(false);
    if (isEditMassUpload) {
      setMassUploadData(
        massUploadData?.map((item) => {
          if (item.id === row?.id) {
            return { ...item, expandRow: !item.expandRow };
          } else {
            return item;
          }
        })
      );
    } else {
      setMassUploadData();
    }
    setSlidingScaleFormData({
      fields: [
        {
          premiumRangeStart: null,
          premiumRangeEnd: null,
          percentageRate: null,
          index: 0,
        },
      ],
    });
    setCommissions([]);
    setNewAttachmentRow();
    setAgmtSourceFiles();
    setFilesForUpload();
    setToExpire(false);
  };

  const handleAllSelectChange = (
    selected,
    options,
    allOption,
    allActive,
    setAllActive,
    previousSelected,
    setPreviousSelected
  ) => {
    let updated = selected || [];

    // Selected "ALL"
    if (updated.some((x) => x.value === "ALL")) {
      if (
        updated.length === 1 ||
        updated.filter((x) => x.value !== "ALL").length ===
          previousSelected.length ||
        (updated.filter((x) => x.value !== "ALL").length ===
          rowForEdit.state?.length &&
          isEditState) ||
        (updated.filter((x) => x.value !== "ALL").length ===
          rowForEdit.policyType?.length &&
          isEditState)
      ) {
        // Only ALL → select every option
        updated = [allOption, ...options];
        setAllActive(true);
      } else {
        // ALL + others → treat as uncheck of ALL
        updated = updated.filter((x) => x.value !== "ALL");
        setAllActive(false);
      }
    }
    // Unchecked ALL (was active before)
    else if (allActive) {
      updated = [];
      setAllActive(false);
    }
    // Manual selection
    else {
      if (updated.length === options.length) {
        updated = [allOption, ...options];
        setAllActive(true);
      } else {
        setAllActive(false);
      }
    }

    // Track previously selected (excluding ALL)
    setPreviousSelected(updated.filter((x) => x.value !== "ALL"));

    // Return final updated list for the onchange controller
    return updated;
  };

  const handleClickConflictingAgreements = (event) => {
    setSavedAgreementData({
      ...agreementData,
      state: Array.isArray(agreementData.state)
        ? agreementData.state.map((x) =>
            typeof x === "string" ? { label: x, value: x } : x
          )
        : agreementData.state
        ? [{ label: agreementData.state, value: agreementData.state }]
        : [],
      policyType: Array.isArray(agreementData.policyType)
        ? agreementData.policyType.map((x) =>
            typeof x === "string" ? { label: x, value: x } : x
          )
        : agreementData.policyType
        ? [{ label: agreementData.policyType, value: agreementData.policyType }]
        : [],
      origData: rowForEdit,
      isEdit: isEditState,
      isAdd: isAddState,
    });

    setFilterConflictingAgreements(true);
    setOpenConflictingDate(false);
    setShowGroupView(false);
    setEditState(false);
    setAddState(false);
    setEditMassUpload(false);
    setToExpire(false);

    queryClient.invalidateQueries("GetMainAgreements");
    getAgmtRefetch();

    event.preventDefault();
  };

  return (
    <div className="relative">
      <div className="divide-y">
        <div className="px-4 pb-8 pt-3 border-l-2 border-primary  box-shadow">
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <div className="flex flex-row justify-between items-center text-primary font-bold font-sans text-lg pb-1 border-b-2 border-secondary">
              <div>
                {isAddState ? (
                  "Add Agreement"
                ) : (
                  <span>Edit Agreement {rowForEdit.agreementRepositoryId}</span>
                )}
              </div>

              <div className="flex justify-between gap-2 pb-1">
                <SubmitHookForm
                  type="submit"
                  disabled={isSaving}
                  buttonText={isSaving ? "Saving.." : "Save"}
                />
                <CancelButton
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>

            <div className="flex justify-between my-3">
              <div className="text-xs text-secondary italic font-normal">
                <b> Note:</b> All fields with * are required to fill.
              </div>
              <div>
                {user.isFranchise && user.isNonFranchise && (
                  <div className="flex flex-row items-center gap-2 px-2">
                    <label className="flex text-xs lg:justify-end sm:justify-start">
                      Franchise:
                    </label>
                    <CheckBoxHookForm
                      className="w-4 h-4"
                      name="isFranchise"
                      label="Franchise"
                      type="checkbox"
                      disabled={
                        (!user.isFranchise && !user.isNonFranchise) ||
                        (user.isFranchise && !user.isNonFranchise)
                      }
                      register={register}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="bg-white box-shadow rounded-lg pb-3 grid-rows-2">
              <div className="space-y-2 p-2">
                <div className="grid grid-cols-1 md:grid-cols-3 items-center w-full gap-x-12 gap-y-2 p-2">
                  <div className="flex flex-row items-center space-x-1">
                    <Controller
                      control={control}
                      name="masterCarrierId"
                      rules={{
                        required: !toExpire,
                        validate: (value) =>
                          carrierOptions?.findIndex((c) => c.value === value) >=
                          0,
                      }}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          label="Carrier"
                          field={field}
                          onChange={(e) => {
                            field.onChange(e ? e.value : null);
                            setValue("carrier", e?.label);
                            setValue("program", null);
                          }}
                          value={field.value ? field.value : null}
                          options={carrierOptions}
                          isLoading={carriersLoading}
                          isMulti={false}
                          className={
                            errors.masterCarrierId &&
                            "border-2 border-red-500 col-span-2"
                          }
                          required={true}
                        />
                      )}
                    />
                  </div>
                  <>
                    <Controller
                      name="agency"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          field={field}
                          onChange={(e) => {
                            field.onChange(e ? e.value : null);
                            setValue(
                              "agencyMappingId",
                              e ? e?.agencyMappingId : null
                            );
                          }}
                          value={field.value}
                          isLoading={agencyLoading}
                          options={filteredAgenciesWithAll}
                          label="Agency"
                          className={
                            errors.agency &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          isDisabled={
                            watch("state") && watch("state").length > 0
                              ? false
                              : true
                          }
                          required={true}
                          placeHolder={
                            (watch("state") && watch("state").length < 1) ||
                            !watch("state")
                              ? "Select State/s to enable"
                              : watch("agency")
                          }
                        />
                      )}
                    />
                    <div className="space-y-1 w-full text-xs">
                      <div className="grid grid-cols-1 md:grid-cols-3 w-full items-center gap-2">
                        <label>Agency Details:</label>
                        <button
                          type="button"
                          className="col-span-2 text-left text-quinary underline"
                          onClick={() => setAgencyShow(true)}
                        >
                          View Per State
                        </button>
                      </div>
                    </div>
                  </>
                  <div className="relative">
                    <Controller
                      control={control}
                      name="state"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectHookFormMulti
                          field={field}
                          onChange={(e) => {
                            const updated = handleAllSelectChange(
                              e,
                              stateOptions,
                              allDropdownOption,
                              allState,
                              setAllState,
                              previousSelected,
                              setPreviousSelected
                            );
                            field.onChange(updated);
                            statesOnChange(e);
                          }}
                          value={field.value}
                          options={allStatesOpt}
                          isLoading={statesLoading}
                          label="State"
                          className={
                            errors.state &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          isRequired={true}
                        />
                      )}
                    />
                    <Tooltip
                      id="states"
                      place="top"
                      effect="solid"
                      type="info"
                      border={true}
                      arrowColor="#0078d7"
                      borderColor="#0078d7"
                      backgroundColor="white"
                      textColor="#0078d7"
                    />
                    <InformationCircleIcon
                      data-tip={
                        "Changing state will reset Agency, Legal Entity and Sales Lead field."
                      }
                      data-for="states"
                      className="absolute top top-1 right-0 h-5 w-5 -mr-5 text-primary"
                    />
                  </div>
                  <>
                    <Controller
                      control={control}
                      name="entityName"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          field={field}
                          onChange={(e) => {
                            field.onChange(e ? e.value : null);
                            setValue("entityId", e.entityId);
                          }}
                          value={field.value}
                          isLoading={entityLoading}
                          options={filteredEntityOpt}
                          label="Legal Entity"
                          isDisabled={
                            watch("state") && watch("state").length > 0
                              ? false
                              : true
                          }
                          className={
                            errors.entityName &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          required={true}
                          placeHolder={
                            (watch("state") && watch("state").length < 1) ||
                            !watch("state")
                              ? "Select State/s to enable"
                              : watch("entityName")
                          }
                        />
                      )}
                    />
                    <div className="space-y-1 w-full text-xs">
                      <div className="grid grid-cols-1 md:grid-cols-3 w-full items-center gap-2">
                        <label>Entity Details:</label>
                        <button
                          type="button"
                          className="col-span-2 text-left text-quinary underline"
                          onClick={() => setEntityModalShow(true)}
                        >
                          View Per State
                        </button>
                      </div>
                    </div>
                  </>
                  <Controller
                    name="paidType"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={paidTypeOptions}
                        label="Commission Paid As"
                        className={
                          errors.paidType &&
                          "border-2 border-red-500 col-span-2 "
                        }
                        required={true}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="masterLegalEntityId"
                    rules={{ required: false }}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                          setValue("masterEntityName", e?.label);
                        }}
                        value={field.value}
                        options={masterLegalEntityOpt}
                        label="Master Legal Entity"
                        className={
                          errors.masterLegalEntityId &&
                          "border-2 border-red-500 col-span-2 "
                        }
                        required={false}
                        isLoading={masterLegalEntityLoading}
                      />
                    )}
                  />

                  <Controller
                    name="billing"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={billingTypes}
                        label="Billing"
                        className={
                          errors.billing &&
                          "border-2 border-red-500 col-span-2 "
                        }
                        required={true}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="policyType"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <SelectHookFormMulti
                        field={field}
                        onChange={(e) => {
                          const updated = handleAllSelectChange(
                            e,
                            policyType,
                            allDropdownOption,
                            allPolicy,
                            setAllPolicy,
                            previousSelected,
                            setPreviousSelected
                          );
                          field.onChange(updated);
                        }}
                        value={field.value}
                        options={policyType}
                        isLoading={policyLoading}
                        label="Policy Type"
                        className={
                          errors.policyType &&
                          "border-2 border-red-500 col-span-2"
                        }
                        isRequired={true}
                      />
                    )}
                  />

                  <TextInputLabelHookForm
                    name="effectiveDate"
                    label="Effective Date"
                    type="date"
                    register={register("effectiveDate", {})}
                    onChange={(e) => {
                      if (e.target.value) {
                        setMinExpire(e.target.value);
                      }
                    }}
                  />
                  <TextInputLabelHookForm
                    name="expirationDate"
                    label="Expiration Date"
                    type="date"
                    id="expirationDate"
                    min={minExpire}
                    register={register("expirationDate", {})}
                    onChange={(e) => {
                      if (e.target.value) {
                        setToExpire(true);
                      } else {
                        setToExpire(false);
                      }
                    }}
                  />
                  <TextInputLabelHookForm
                    name="commissionEffectiveDate"
                    label="Day Commission Stmt Available"
                    type="date"
                    register={register("commissionEffectiveDate", {})}
                  />
                  <Controller
                    name="agreementIsActive"
                    rules={{
                      validate: (value) => value === true || value === false,
                    }}
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={statusTypes}
                        label="Agreement Status"
                        className={
                          errors.agreementIsActive &&
                          "border-2 border-red-500 col-span-2 "
                        }
                        required={true}
                      />
                    )}
                  />

                  {userPermissions.includes(comValStatusPermssion) && (
                    <Controller
                      name="isActive"
                      rules={{
                        validate: (value) => value === true || value === false,
                      }}
                      control={control}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          field={field}
                          onChange={(e) => {
                            field.onChange(e ? e.value : null);
                          }}
                          value={field.value}
                          required={true}
                          options={comValStatusTypes}
                          label="Used In Commval"
                          className={
                            errors.isActive &&
                            "border-2 border-red-500 col-span-2 "
                          }
                        />
                      )}
                    />
                  )}
                  <Controller
                    name="program"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        isLoading={programsLoading}
                        options={filteredProgramOptions}
                        label="Program Name"
                        isDisabled={watch("masterCarrierId") ? false : true}
                        placeHolder={
                          !watch("masterCarrierId")
                            ? "Select Carrier to enable"
                            : undefined
                        }
                      />
                    )}
                  />
                  <Controller
                    name="frequencyOfCommissionPaymentsId"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={commPaymentFreqOpt}
                        label="Frequency of Commission Payments"
                      />
                    )}
                  />
                  <Controller
                    name="businessTypeId"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                          setValue("businessTypeName", e?.label);
                          setValue("lineOfBusinessId", null);
                        }}
                        value={field.value}
                        isLoading={businessLoading}
                        options={businessTypesOptions}
                        label="Business Type"
                      />
                    )}
                  />
                  <Controller
                    name="lineOfBusinessId"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        isLoading={lobsLoading}
                        options={lobOptions}
                        label="Line of Business"
                        isDisabled={!watch("businessTypeId")}
                        placeHolder={
                          watch("businessTypeId") < 1 ||
                          !watch("businessTypeId")
                            ? "Select Business Type to enable"
                            : undefined
                        }
                      />
                    )}
                  />
                  <Controller
                    name="standard"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={standardTypes}
                        label="Standard"
                      />
                    )}
                  />
                  <Controller
                    name="licensedTypeId"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={licensedTypes}
                        label="Licensed Type"
                      />
                    )}
                  />
                  <TextInputLabelHookForm
                    name="producerCode"
                    label="Producer Code"
                    type="text"
                    register={register("producerCode", {})}
                  />
                  <TextInputLabelHookForm
                    name="policyPrefix"
                    label="Policy Prefix"
                    type="text"
                    register={register("policyPrefix", {})}
                  />
                  <Controller
                    name="agreementType"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={agreementTypeOptions}
                        label="Agreement Type"
                      />
                    )}
                  />

                  <Controller
                    name="paymentRemittanceTypeId"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={paymentRemittanceTypesOptions}
                        label="Payment Remittance Type"
                      />
                    )}
                  />
                  <Controller
                    name="planCode"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                        }}
                        value={field.value}
                        options={appointedByOptions}
                        label="Appointed By"
                      />
                    )}
                  />
                  <Controller
                    name="salesLead"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                          setValue("salesLeadName", e ? e.employeeName : "");
                          setValue("salesLeadEmployeeId", e ? e.value : "");
                        }}
                        value={field.value}
                        isLoading={employeesLoading}
                        options={filteredSalesLEad}
                        label="Sales Lead"
                        isDisabled={
                          watch("state") && watch("state").length > 0
                            ? false
                            : true
                        }
                      />
                    )}
                  />
                  <Controller
                    name="riskTypeId"
                    control={control}
                    render={({ field }) => (
                      <SelectHookFormSingle
                        field={field}
                        onChange={(e) => {
                          field.onChange(e ? e.value : null);
                          setValue("riskTypeName", e ? e.label : "");
                        }}
                        value={field.value}
                        options={riskTypes}
                        label="Risk Type"
                        isLoading={riskLoading}
                      />
                    )}
                  />
                  <TextInputLabelHookForm
                    name="notes"
                    label="Notes"
                    type="text"
                    register={register("notes", {})}
                  />

                  <CheckBoxLabelHookForm
                    name="contingentCommission"
                    label="Contingent Commission"
                    type="checkbox"
                    register={register}
                  />
                  <CheckBoxLabelHookForm
                    name="flatRatePerPolicy"
                    label="Flat Rate Per Policy"
                    type="checkbox"
                    register={register}
                    onClick={() => {
                      setValue("newBusinessFlatRate", null);
                      setValue("renewalFlatRate", null);
                      setValue("newPercentage", 0);
                      setValue("renewalPercentage", 0);
                      setValue("newPercentageConfie", 0);
                      setValue("newPercentageFranchise", 0);
                      setValue("renewPercentageConfie", 0);
                      setValue("renewPercentageFranchise", 0);
                    }}
                  />
                  <CheckBoxLabelHookForm
                    name="isWritingNewBusiness"
                    label="Currently Writing New Business"
                    type="checkbox"
                    register={register}
                  />
                  {!isEditMassUpload && (
                    <CheckBoxLabelHookForm
                      name="isSplitRate"
                      label="Split Rates Liability/ Physical"
                      type="checkbox"
                      register={register}
                      onClick={(e) => {
                        const checked = e.target.checked;
                        if (!checked) {
                          setCurrentSplitRateValue(false);
                          setValue("renewalPhysicalDamageRate", null);
                          setValue("newBusinessLiabilityRate", null);
                          setValue("renewalLiabilityRate", null);
                          setValue("newBusinessPhysicalDamageRate", null);
                        } else {
                          setCurrentSplitRateValue(true);
                        }
                      }}
                    />
                  )}

                  {user.isFranchise && watch("isFranchise") && (
                    <Controller
                      name="commissionRemittanceId"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <SelectHookFormSingle
                          field={field}
                          onChange={(e) => {
                            field.onChange(e ? e.value : null);
                            setValue("commissionRemittance", e ? e.label : "");
                          }}
                          value={field.value}
                          options={commissionRemittanceOption}
                          label="Commission Remittance"
                          className={
                            errors.commissionRemittanceId &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          required={user.isFranchise}
                        />
                      )}
                    />
                  )}

                  {watch("flatRatePerPolicy") ? (
                    <>
                      <div className="sm:col-start-2">
                        <NumericInputLabel
                          name="newBusinessFlatRate"
                          label="New Flat Rate"
                          register={register("newBusinessFlatRate", {
                            required: {
                              value:
                                watch("flatRatePerPolicy") && !isOtherChecked,
                              message: "Required Field",
                            },
                          })}
                          required={
                            watch("flatRatePerPolicy") && !isOtherChecked
                          }
                          pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
                          type="number"
                          step="0.01"
                          min="0"
                          max="999999.99"
                          className={
                            errors.newBusinessFlatRate &&
                            "border-2 border-red-500 col-span-2 "
                          }
                        />
                      </div>
                      <NumericInputLabel
                        name="renewalFlatRate"
                        label="Renewal Flat Rate"
                        register={register("renewalFlatRate", {
                          required: {
                            value:
                              watch("flatRatePerPolicy") && !isOtherChecked,
                            message: "Required Field",
                          },
                        })}
                        required={watch("flatRatePerPolicy") && !isOtherChecked}
                        pattern="/^\[0-9]{6}\.?\[0-9{2}$/i,"
                        type="number"
                        step="0.01"
                        min="0"
                        max="999999.99"
                        className={
                          errors.renewalFlatRate &&
                          "border-2 border-red-500 col-span-2 "
                        }
                      />
                    </>
                  ) : (
                    <>
                      <div className="sm:col-start-2">
                        <NumericInputLabel
                          name="newPercentage"
                          label="New Percentage"
                          register={register("newPercentage", {
                            required: {
                              value: !isOtherChecked,
                              message: "Required Field",
                            },
                          })}
                          required={!isOtherChecked}
                          pattern="/^\d*\.?\d*$/i,"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100.00"
                          className={
                            errors.newPercentage &&
                            errors.newPercentage.message &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          onChange={(e) => {
                            setValue("newPercentageFranchise", 0);
                            setValue("newPercentageConfie", 0);
                          }}
                          onBlur={(e) => {
                            if (e.target.value) {
                              setValue(
                                "newPercentage",
                                parseFloat(e.target.value).toFixed(2),
                                {
                                  shouldDirty:
                                    parseFloat(e.target.value).toFixed(2) !==
                                    rowForEdit?.newPercentage
                                      ? true
                                      : false,
                                }
                              );
                            } else {
                              setValue("newPercentage", null);
                            }
                          }}
                        />
                      </div>
                      <div>
                        <NumericInputLabel
                          name="renewalPercentage"
                          label="Renewal Percentage"
                          register={register("renewalPercentage", {
                            required: {
                              value: !isOtherChecked,
                              message: "Required Field",
                            },
                          })}
                          required={!isOtherChecked}
                          pattern="/^\d*\.?\d*$/i,"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100.00"
                          className={
                            errors.renewalPercentage &&
                            errors.renewalPercentage.message &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          onChange={(e) => {
                            setValue("renewPercentageFranchise", 0);
                            setValue("renewPercentageConfie", 0);
                          }}
                          onBlur={(e) => {
                            if (e.target.value) {
                              setValue(
                                "renewalPercentage",
                                parseFloat(e.target.value).toFixed(2),
                                {
                                  shouldDirty:
                                    parseFloat(e.target.value).toFixed(2) !==
                                    rowForEdit?.renewalPercentage
                                      ? true
                                      : false,
                                }
                              );
                            }
                          }}
                        />
                      </div>

                      {user.isFranchise && watch("isFranchise") && (
                        <>
                          <div className="sm:col-start-2 grid grid-cols-1 md:grid-cols-2 justify-items-end gap-1">
                            <div className="md:col-start-1 w-full">
                              <NumericInputLabel
                                name="newPercentageConfie"
                                label="Confie"
                                register={register("newPercentageConfie", {
                                  required: {
                                    value: !isOtherChecked,
                                    message: "Required Field",
                                  },
                                })}
                                required={user.isFranchise && !isOtherChecked}
                                pattern="/^\d*\.?\d*$/i,"
                                type="number"
                                step="0.01"
                                min="0"
                                max={watch("newPercentage")}
                                className={
                                  errors.newPercentageConfie &&
                                  errors.newPercentageConfie.message &&
                                  "border-2 border-red-500 col-span-2 "
                                }
                                secondDiv="grid grid-cols-1"
                                onChange={(e) => {
                                  if (
                                    watch("isFranchise") === true ||
                                    !watch("flatRatePerPolicy")
                                  ) {
                                    const newFranchise =
                                      watch("newPercentage") - e.target.value;

                                    setValue(
                                      "newPercentageFranchise",
                                      newFranchise?.toFixed(2)
                                    );
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    setValue(
                                      "newPercentageConfie",
                                      parseFloat(e.target.value).toFixed(2),
                                      {
                                        shouldDirty:
                                          parseFloat(e.target.value).toFixed(
                                            2
                                          ) !== rowForEdit?.newPercentageConfie
                                            ? true
                                            : false,
                                      }
                                    );
                                  }
                                }}
                              />
                            </div>
                            <NumericInputLabel
                              name="newPercentageFranchise"
                              label="Franchise"
                              disabled
                              register={register("newPercentageFranchise", {
                                required: {
                                  value: !isOtherChecked,
                                  message: "Required Field",
                                },
                              })}
                              required={user.isFranchise && !isOtherChecked}
                              pattern="/^\d*\.?\d*$/i,"
                              type="number"
                              step="0.01"
                              min={
                                watch("newPercentageConfie") > 0
                                  ? watch("newPercentage") -
                                    watch("newPercentageConfie")
                                  : 0
                              }
                              max={
                                watch("newPercentageConfie") > 0
                                  ? watch("newPercentage") -
                                    watch("newPercentageConfie")
                                  : watch("newPercentage")
                              }
                              onBlur={(e) => {
                                if (e.target.value) {
                                  setValue(
                                    "newPercentageFranchise",
                                    parseFloat(e.target.value).toFixed(2),
                                    {
                                      shouldDirty:
                                        parseFloat(e.target.value).toFixed(
                                          2
                                        ) !== rowForEdit?.newPercentageFranchise
                                          ? true
                                          : false,
                                    }
                                  );
                                }
                              }}
                              className={
                                errors.newPercentageFranchise &&
                                errors.newPercentageFranchise.message &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              secondDiv="grid grid-cols-1"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 justify-items-end gap-1">
                            <div className="md:col-start-1 w-full">
                              <NumericInputLabel
                                name="renewPercentageConfie"
                                label="Confie"
                                register={register("renewPercentageConfie", {
                                  required: {
                                    value: !isOtherChecked,
                                    message: "Required Field",
                                  },
                                })}
                                required={user.isFranchise && !isOtherChecked}
                                pattern="/^\d*\.?\d*$/i,"
                                type="number"
                                step="0.01"
                                min="0"
                                max={watch("renewalPercentage")}
                                className={
                                  !isOtherChecked
                                    ? errors.renewPercentageConfie &&
                                      errors.renewPercentageConfie.message &&
                                      "border-2 border-red-500 col-span-2 "
                                    : undefined
                                }
                                secondDiv="grid grid-cols-1"
                                onChange={(e) => {
                                  if (
                                    watch("isFranchise") === true ||
                                    !watch("flatRatePerPolicy")
                                  ) {
                                    const renewFranchise =
                                      watch("renewalPercentage") -
                                      e.target.value;

                                    setValue(
                                      "renewPercentageFranchise",
                                      renewFranchise?.toFixed(2)
                                    );
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value) {
                                    setValue(
                                      "renewPercentageConfie",
                                      parseFloat(e.target.value).toFixed(2),
                                      {
                                        shouldDirty:
                                          parseFloat(e.target.value).toFixed(
                                            2
                                          ) !==
                                          rowForEdit?.renewPercentageConfie
                                            ? true
                                            : false,
                                      }
                                    );
                                  }
                                }}
                              />
                            </div>
                            <NumericInputLabel
                              name="renewPercentageFranchise"
                              label="Franchise"
                              disabled
                              register={register("renewPercentageFranchise", {
                                required: {
                                  value: !isOtherChecked,
                                  message: "Required Field",
                                },
                              })}
                              required={user.isFranchise && !isOtherChecked}
                              pattern="/^\d*\.?\d*$/i,"
                              type="number"
                              step="0.01"
                              min={
                                watch("renewPercentageConfie") > 0
                                  ? watch("renewalPercentage") -
                                    watch("renewPercentageConfie")
                                  : 0
                              }
                              max={
                                watch("renewPercentageConfie") > 0
                                  ? watch("renewalPercentage") -
                                    watch("renewPercentageConfie")
                                  : watch("renewalPercentage")
                              }
                              onBlur={(e) => {
                                if (e.target.value) {
                                  setValue(
                                    "renewPercentageFranchise",
                                    parseFloat(e.target.value).toFixed(2),
                                    {
                                      shouldDirty:
                                        parseFloat(e.target.value).toFixed(
                                          2
                                        ) !==
                                        rowForEdit?.renewPercentageFranchise
                                          ? true
                                          : false,
                                    }
                                  );
                                }
                              }}
                              className={
                                errors.renewPercentageFranchise &&
                                errors.renewPercentageFranchise.message &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              secondDiv="grid grid-cols-1"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {!isEditMassUpload && (
                    <CheckBoxLabelHookForm
                      name="isSlidingScale"
                      label="Sliding Scale"
                      type="checkbox"
                      register={register}
                      onClick={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setIsSlidingScaleChecked(true);
                        } else {
                          setIsSlidingScaleChecked(false);
                          setSlidingScaleFormData({
                            fields: [
                              {
                                premiumRangeStart: null,
                                premiumRangeEnd: null,
                                percentageRate: null,
                                index: 0,
                              },
                            ],
                          });
                        }
                      }}
                    />
                  )}

                  <div></div>
                  <div></div>
                  <div className="py-2">
                    <CheckBoxLabelHookForm
                      name="isOverridePayment"
                      label="Override Commission"
                      type="checkbox"
                      register={register}
                      onClick={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setIsOverridePayment(true);
                        } else {
                          setIsOverridePayment(false);
                          setValue("overridePaymentTypeId", null);
                          setValue("overridePercentage", 0);
                          setValue("overrideRenewalPercentage", 0);
                        }
                      }}
                    />
                  </div>

                  {watch("isOverridePayment") && (
                    <>
                      <div>
                        <Controller
                          control={control}
                          name="overridePaymentTypeId"
                          rules={{ required: true }}
                          render={({ field }) => (
                            <SelectHookFormSingle
                              field={field}
                              onChange={(e) => {
                                field.onChange(e ? e.value : null);
                                setValue("overridePaymentTypeId", e?.value);
                              }}
                              value={field.value}
                              options={overridePaymentTypeOpt}
                              label="Override Payable"
                              className={
                                errors.overridePaymentTypeId &&
                                "border-2 border-red-500 col-span-2 "
                              }
                              required={true}
                              isLoading={overridePaymentTypeLoading}
                            />
                          )}
                        />
                      </div>
                      <div></div>
                      <div></div>
                      <div>
                        <NumericInputLabel2
                          name="overridePercentage"
                          label="New Percentage"
                          register={register("overridePercentage", {
                            required: {
                              value: isOverridePayment,
                              message: "Required Field",
                            },
                          })}
                          required={isOverridePayment}
                          pattern="/^\d*\.?\d*$/i,"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100.00"
                          className={
                            errors.overridePercentage &&
                            errors.overridePercentage.message &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          onBlur={(e) => {
                            if (e.target.value) {
                              setValue(
                                "overridePercentage",
                                parseFloat(e.target.value).toFixed(2),
                                {
                                  shouldDirty:
                                    parseFloat(e.target.value).toFixed(2) !==
                                    rowForEdit?.overridePercentage
                                      ? true
                                      : false,
                                }
                              );
                            } else {
                              setValue("overridePercentage", 0);
                            }
                          }}
                        />
                      </div>
                      <div></div>
                      <div></div>
                      <div>
                        <NumericInputLabel2
                          name="overrideRenewalPercentage"
                          label="Renewal Percentage"
                          register={register("overrideRenewalPercentage", {
                            required: {
                              value: isOverridePayment,
                              message: "Required Field",
                            },
                          })}
                          required={isOverridePayment}
                          pattern="/^\d*\.?\d*$/i,"
                          type="number"
                          step="0.01"
                          min="0"
                          max="100.00"
                          className={
                            errors.overrideRenewalPercentage &&
                            errors.overrideRenewalPercentage.message &&
                            "border-2 border-red-500 col-span-2 "
                          }
                          onBlur={(e) => {
                            if (e.target.value) {
                              setValue(
                                "overrideRenewalPercentage",
                                parseFloat(e.target.value).toFixed(2),
                                {
                                  shouldDirty:
                                    parseFloat(e.target.value).toFixed(2) !==
                                    rowForEdit?.overrideRenewalPercentage
                                      ? true
                                      : false,
                                }
                              );
                            } else {
                              setValue("overrideRenewalPercentage", 0);
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
                {isSlidingScaleChecked && (
                  <div>
                    <label className="text-xs italic mx-2">
                      Sliding Scale Related Fields
                    </label>
                    <div className="grid gap-10 p-4 bg-gray-50 rounded-md border border-gray-200 mt-1 mx-2">
                      <div>
                        <SlidingScaleForm />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
          {!isEditMassUpload && (
            <div className="grid grid-cols-2 space-x-2 space-y-2 divide-x-1.5 ">
              <div className="mt-2 bg-white box-shadow rounded-lg px-4 pb-4">
                {otherCommsLoading ? (
                  <CustomLoader />
                ) : (
                  <AddCommissionDetails
                    commissions={commissions}
                    setCommissions={setCommissions}
                    isAdd={true}
                  />
                )}
              </div>
              <div className="mr-2 bg-white box-shadow rounded-lg pb-3">
                <AddAttachmentsButton
                  filesForUpload={filesForUpload}
                  setFilesForUpload={setFilesForUpload}
                  agmtSourceFiles={agmtSourceFiles}
                  setAgmtSourceFiles={setAgmtSourceFiles}
                  newAttachmentRow={newAttachmentRow}
                  setNewAttachmentRow={setNewAttachmentRow}
                  isEdit={isEditState}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {entityModalShow && (
        <EntityModal
          isOpen={entityModalShow}
          onClose={setEntityModalShow}
          entityData={legalEntities}
        />
      )}
      {agencyShow && (
        <AgencyModal
          agencyData={agencyDetails}
          selectedAgency={watch("agency")}
          isOpen={agencyShow}
          onClose={() => setAgencyShow(false)}
        />
      )}

      <ConfirmBoxModal
        ModalTitle={isAddState ? "Add Agreement" : "Edit Agreement"}
        qConfirmation={`Are you sure you want to ${
          isAddState
            ? "save "
            : toExpire
            ? rowForEdit.expirationDate === null
              ? "update and expire "
              : "update and renew"
            : "update "
        } this agreement?`}
        isOpen={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onClickFunction={() => {
          toExpire && !isEditMassUpload
            ? setOpenConfirmNew(true)
            : handleSave(true);
        }}
      />
      <ConfirmBoxYesNo
        ModalTitle={"Renew Agreement"}
        qConfirmation={`Do you want to create new Agreement for this Carrier?`}
        isOpen={openConfirmNew}
        onClose={() => {
          handleSave(true);
        }}
        onClickFunction={() => {
          handleRenewAgreements(true, true);
        }}
      />
      <ConflictingDatesModal
        ModalTitle={"Conflicting Effective Dates"}
        qConfirmation={`There is an existing agreement with conflicting effective dates. Please set the Effective date to other dates or Expired the other agreement`}
        isOpen={openConflictingDate}
        onClose={() => {
          setOpenConflictingDate(false);
          setIsSaving(false);
        }}
        onClickFunction={handleClickConflictingAgreements}
        onClickProceed={() => {
          toExpire && !isEditMassUpload
            ? handleRenewAgreements(false, true)
            : handleSave(false);
          setOpenConflictingDate(false);
        }}
      />
    </div>
  );
};

export default AgreementForm;
